import { useGLTF, useTexture } from "@react-three/drei";
import { useEffect, useRef, useState } from "react";
import { MeshBasicMaterial, MeshStandardMaterial, RepeatWrapping, SRGBColorSpace, Box3, Vector3 } from "three";
import InfoPanel from "./InfoPanel";
import { loadUnifiedModel } from "../utils/unifiedModelLoader";
import { loadObjModel } from "../utils/objLoader";
import { createPortal } from "react-dom";

const UnifiedModel = (props) => {
    const ref = useRef();
    const [modelData, setModelData] = useState(null);
    const [scene, setScene] = useState(null);
    const [initialMaterialData, setInitialMaterialData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const uvCheckerMap = useTexture('./textures/UVChecker.png');
    const loadedPatterns = {};

    const utilMats = {
        uvCheckMaterial: new MeshStandardMaterial(),
        wireframeMaterial: new MeshBasicMaterial(),
        patternMaterial: new MeshStandardMaterial()
    }

    const initUtilMats = () => {
        if (uvCheckerMap) {
            utilMats.uvCheckMaterial.name = "UV-Check-Material";
            utilMats.uvCheckMaterial.map = uvCheckerMap;
            utilMats.uvCheckMaterial.map.colorSpace = SRGBColorSpace;
            utilMats.uvCheckMaterial.map.wrapS = RepeatWrapping;
            utilMats.uvCheckMaterial.map.wrapT = RepeatWrapping;
            utilMats.uvCheckMaterial.map.flipY = false;
            utilMats.uvCheckMaterial.map.repeat.set(props.uvScale, props.uvScale);
            utilMats.uvCheckMaterial.roughness = 1.0;
        }

        utilMats.wireframeMaterial.name = "Wireframe-Material";
        utilMats.wireframeMaterial.color.setRGB(0.05, 0.05, 0.05);
        utilMats.wireframeMaterial.wireframe = true;

        utilMats.patternMaterial.name = "Pattern-Material";
        props.patternOptions.map((pattern) => {
            initPatternMap(pattern);
        })
    }

    const initPatternMap = (pattern) => {
        let map = useTexture(pattern.image);
        map.colorSpace = SRGBColorSpace;
        map.wrapS = RepeatWrapping;
        map.wrapT = RepeatWrapping;
        map.flipY = false;
        map.repeat.set(props.xRepeat, props.yRepeat);
        map.roughness = 1.0;
        loadedPatterns[pattern.value] = map;
    }

    initUtilMats()

    const UpdateMaterial = () => {
        if (!ref.current) return;
        
        const targetScene = scene || ref.current;
        targetScene.traverse((child) => {
            if (child.isMesh) {
                if (props.wireframe) {
                    child.material = utilMats.wireframeMaterial;
                }
                else if (props.uvChecker) {
                    if (utilMats.uvCheckMaterial && utilMats.uvCheckMaterial.map) {
                        child.material = utilMats.uvCheckMaterial;
                    } else {
                        child.material = initialMaterialData[child.name] || child.material;
                    }
                }
                else if (props.patternsMode && props.selectedPattern && initialMaterialData[child.name]?.name == props.selectedMaterialNode) {
                    utilMats.patternMaterial.map = loadedPatterns[props.selectedPattern];
                    utilMats.patternMaterial.map.rotation = props.selectedPatternRotation;
                    child.material = utilMats.patternMaterial;
                }
                else {
                    child.material = initialMaterialData[child.name];
                }
            }
        });
    }

    const constructTreeData = (node) => {
        let obj = {};

        obj.title = node.name;
        obj.key = node.name;

        if (node.isMesh) {
            obj.material = node.material.name;
        }

        if (node.children.length > 0) {
            obj.children = [];
            for (let i = 0; i < node.children.length; i++) {
                let childObj = constructTreeData(node.children[i]);
                obj.children.push(childObj);
                if (childObj.key && props.metadata && props.metadata[childObj.key]) {
                    const childNode = {title:<InfoPanel
                        nodeClass={props.metadata[childObj.key].class}
                        vertexCount={props.metadata[childObj.key].vertex_count}
                        polygonCount={props.metadata[childObj.key].polygon_count}
                        quadsCount={props.metadata[childObj.key].quads}
                        triCount={props.metadata[childObj.key].tri}
                        ngonsCount={props.metadata[childObj.key].ngons}
                        materialName={props.metadata[childObj.key].material_name}
                        materialClass={props.metadata[childObj.key].material_class}
                        modifiers={props.metadata[childObj.key].modifiers}
                        texelDensity={props.metadata[childObj.key].texel_density}
                    />, checkable:false}
                    
                    if (childObj.children) {
                        childObj.children.unshift(childNode);
                    }
                    else {
                        childObj.children = [childNode]
                    }
                }
            }
        }

        return obj;
    }

    const convertMaterials = () => {
        if (scene && ref.current) {
            const box = new Box3().setFromObject(scene);
            const size = box.getSize(new Vector3())
            const initialMaterialData = {};
            
            const maxDimension = Math.max(size.x, size.y, size.z);
            if (maxDimension > 1) {
                const scale = 1 / maxDimension;
                scene.scale.setScalar(scale);
            }
            
            scene.traverse((child) => {
                if (child.isMesh && child.material) {
                    if (child.material.type === 'MeshPhongMaterial') {
                        const newMaterial = new MeshStandardMaterial({
                            name: child.material.name,
                            color: child.material.color,
                            map: child.material.map,
                            roughness: 0.5,
                            metalness: 0.0,
                            transparent: false,
                            opacity: 1.0
                        });
                        child.material = newMaterial;
                        initialMaterialData[child.name] = newMaterial;
                    }
                    
                    child.material.needsUpdate = true;
                } else {
                    initialMaterialData[child.name] = child.material;
                }
            });
            
            const scaledBox = new Box3().setFromObject(scene);
            const scaledSize = scaledBox.getSize(new Vector3());
            const center = scaledBox.getCenter(new Vector3());
            
            props.setModelSize(scaledSize);
            setInitialMaterialData(initialMaterialData);
        }
    }

    const extractMaterialData = () => {
        if (!ref.current) return;
        
        let uniqueMaterialNodes = new Set();
        const targetScene = scene || ref.current;
        targetScene.traverse((child) => {
            if (child.isMesh) {
                if (child.material?.name) {
                    uniqueMaterialNodes.add(child.material.name);
                }
                initialMaterialData[child.name] = child.material;
                setInitialMaterialData(initialMaterialData);
            }
        });
        props.onSetMaterialNodes(uniqueMaterialNodes);
    }

    useEffect(() => {
        const loadModel = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const loadedModelData = await loadUnifiedModel(props.url);
                setModelData(loadedModelData);
                
                if (loadedModelData.type === 'obj') {
                    const loadedScene = await loadObjModel(
                        loadedModelData.objContent, 
                        loadedModelData.mtlContent, 
                        loadedModelData.textures
                    );
                    setScene(loadedScene);
                }
                
                setLoading(false);
                
            } catch (err) {
                console.error('Error loading model:', err);
                const errorMessage = err.message || 'Failed to load model';
                setError(errorMessage);
                
                if (props.onModelError) {
                    props.onModelError(errorMessage);
                } else if (props.onLoadingStatus) {
                    props.onLoadingStatus('error', errorMessage);
                }
                setLoading(false);
            }
        };

        if (props.url) {
            loadModel();
        }
    }, [props.url]);

    const gltf = modelData?.type === 'glb' && modelData?.url ? useGLTF(modelData.url) : null;

    useEffect(() => {
        if (gltf && ref.current) {
            let treeData = [constructTreeData(ref.current.children[0])];
            extractMaterialData();
            props.onModelLoaded(treeData);
        }
    }, [gltf]);

    useEffect(() => {
        if (scene && ref.current) {
            convertMaterials();
            let treeData = [constructTreeData(scene)];
            extractMaterialData();
            props.onModelLoaded(treeData);
        }
    }, [scene]);

    useEffect(() => {
        if (props.visibleNodes != undefined && ref.current) {
            const targetScene = scene || ref.current;
            targetScene.traverse((child) => {
                if (child.isMesh) {
                    if (props.visibleNodes.includes(child.name)) {
                        child.visible = true;
                    }
                    else {
                        child.visible = false;
                    }
                }
            });
        }
    }, [props.visibleNodes]);

    useEffect(() => {
        if (gltf || scene) {
            UpdateMaterial();
            const targetScene = scene || gltf.scene;
            const box = new Box3().setFromObject(targetScene);
            const size = box.getSize(new Vector3());
            props.setModelSize(size);
        }
    }, [gltf, scene, props.uvChecker, props.wireframe, props.uvScale, 
        props.patternsMode, props.selectedPattern, props.selectedPatternRotation, 
        props.selectedMaterialNode, props.xRepeat, props.yRepeat]);

    if (error) {
        console.error('Model loading error:', error);
        return createPortal(
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(255, 0, 0, 0.1)',
                border: '2px solid #ff0000',
                borderRadius: '8px',
                padding: '20px',
                maxWidth: '80%',
                textAlign: 'center',
                color: '#ff0000',
                fontFamily: 'Arial, sans-serif',
                zIndex: 1000
            }}>
                <h3>Error Loading Model</h3>
                <p style={{ whiteSpace: 'pre-line', margin: '10px 0' }}>{error}</p>
                <button 
                    onClick={() => window.location.reload()} 
                    style={{
                        background: '#ff0000',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginTop: '10px'
                    }}
                >
                    Retry
                </button>
            </div>,
            document.body
        );
    }

    if (loading) {
        return null;
    }

    if (!gltf && !scene) {
        return null;
    }

    return (
        <primitive ref={ref} object={scene || gltf.scene} />
    );
};

export default UnifiedModel; 